/**
 * AppBar notification entries.
 *
 * Two distinct attentional categories:
 *  1. MessageSquare → messaging inbox popover (top-N recent unread conversations,
 *     click to jump directly to a conversation). Always visible for authenticated
 *     users; badge only when unread > 0.
 *  2. Bell → system notifications popover for non-messaging events
 *     (on mobile, hidden when unread count is 0; on desktop always visible).
 *
 * This split reflects that human messages and system events are semantically
 * different — follows the Slack/Linear/GitHub AppBar pattern.
 */

import { MessagingInboxPopover } from '../../modules/messaging/components/MessagingInboxPopover';
import { AnnouncementsBell } from '../../modules/software_factory/components/delivery/AnnouncementsBell';
import { SystemNotificationsBell } from './SystemNotificationsBell';

interface AppBarNotificationBadgesProps {
  moduleColor: string;
  /**
   * When false (mobile app bar), the announcements bell is omitted inline —
   * the Header folds it into the three-dots overflow menu instead, so the
   * always-visible bell can't push the user menu off-screen.
   */
  includeAnnouncements?: boolean;
  /**
   * When false (mobile app bar), the messaging inbox is omitted inline — the
   * Header folds it into the three-dots overflow menu instead, giving the
   * module title room to render un-truncated at 375px. Its unread count is
   * rolled up onto the overflow trigger (see useOverflowMenuUnread). System
   * notifications (the Bell) always stay inline.
   */
  includeMessaging?: boolean;
}

export function AppBarNotificationBadges({
  moduleColor,
  includeAnnouncements = true,
  includeMessaging = true,
}: AppBarNotificationBadgesProps) {
  return (
    <>
      {includeMessaging && <MessagingInboxPopover moduleColor={moduleColor} />}
      {includeAnnouncements && <AnnouncementsBell moduleColor={moduleColor} />}
      <SystemNotificationsBell moduleColor={moduleColor} />
    </>
  );
}
